import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
} from '@mui/material';
import { Translate } from 'react-localize-redux';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { getAnaesthesiaActsService, setAnaesthesiaActsService } from '../../../services/billing';

export interface PercentTemplate {
    id: number;
    concept: string;
    percent: number;
}

export interface AnaesthesiaAct {
    id: number;
    concept: string;
    linkedTemplateIds: number[];
}

export interface AnaesthesiaActsPanelProps {
    /** PERCENT_CHARGE templates to associate (e.g. "Anesthésie" at 50%). */
    templates: PercentTemplate[];
    /** Surgical-act billables to choose from, each with the templates it is linked to. */
    acts: AnaesthesiaAct[];
    /** Persist the COMPLETE set of acts linked to the given template. */
    onSave: (templateId: number, selectedIds: number[]) => Promise<void> | void;
    saving?: boolean;
}

/**
 * SHER-342 — pick a percentage template (general anaesthesia is the first one) and the surgical
 * acts that require it; a derived "[act] - {template}" line at the template's percent is then
 * auto-billed for each. Reuses the relatedBillables association — no per-act flag. The percent
 * is the template billable's amount (edited in the billables catalogue). Purely presentational.
 */
export const AnaesthesiaActsPanel: React.FC<AnaesthesiaActsPanelProps> = ({ templates, acts, onSave, saving = false }) => {
    const [query, setQuery] = useState('');
    const [templateId, setTemplateId] = useState<number | ''>(templates[0]?.id ?? '');

    const initial = useMemo(
        () => new Set(acts.filter((a) => templateId !== '' && a.linkedTemplateIds.includes(templateId)).map((a) => a.id)),
        [acts, templateId],
    );
    const [selected, setSelected] = useState<Set<number>>(initial);

    // Re-seed the checkbox state whenever the chosen template (or loaded data) changes.
    useEffect(() => { setSelected(initial); }, [initial]);

    const dirty = useMemo(() => {
        if (selected.size !== initial.size) return true;
        return Array.from(selected).some((id) => !initial.has(id));
    }, [selected, initial]);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        return q ? acts.filter((a) => a.concept.toLowerCase().includes(q)) : acts;
    }, [acts, query]);

    const headCells = [
        { id: 'concept', alignment: 'left', label: <Translate id="hospital.billing.anaesthesia.act_name" /> },
        { id: 'linked', alignment: 'center', label: <Translate id="hospital.billing.anaesthesia.requires_ga" /> },
    ];

    const rows = useMemo(() => {
        return filtered.map((a) => ({
            id: a.id,
            concept: a.concept,
            // "linked" = this act is associated to the selected PERCENT_CHARGE template (no boolean flag).
            linked: selected.has(a.id),
        }));
    }, [filtered, selected]);

    const handleCheckbox = (id: number, _field: string, newValue: boolean) => {
        setSelected((prev) => {
            const next = new Set(prev);
            newValue ? next.add(id) : next.delete(id);
            return next;
        });
    };

    if (templates.length === 0) {
        return (
            <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography variant="body2" color="text.disabled">
                    <Translate id="hospital.billing.anaesthesia.no_templates" />
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel id="anaesthesia-template-label">
                        <Translate id="hospital.billing.anaesthesia.template" />
                    </InputLabel>
                    <Select
                        labelId="anaesthesia-template-label"
                        value={templateId}
                        onChange={(e) => setTemplateId(Number(e.target.value))}
                    >
                        {templates.map((t) => (
                            <MenuItem key={t.id} value={t.id}>
                                {t.concept} ({t.percent}%)
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    label={<Translate id="hospital.billing.anaesthesia.search" />}
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
            </Box>

            {acts.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.disabled">
                        <Translate id="hospital.billing.anaesthesia.empty" />
                    </Typography>
                </Box>
            ) : (
                <EnhancedTable
                    noHeader
                    noSelectable
                    headCells={headCells}
                    rows={rows}
                    actions={[]}
                    callBackCheckbox={handleCheckbox}
                />
            )}

            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                    {selected.size} <Translate id="hospital.billing.anaesthesia.selected_count" />
                </Typography>
                <Button
                    variant="contained"
                    disabled={saving || !dirty || acts.length === 0 || templateId === ''}
                    startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
                    onClick={() => templateId !== '' && onSave(templateId, Array.from(selected))}
                >
                    {saving
                        ? <Translate id="hospital.billing.anaesthesia.saving" />
                        : <Translate id="hospital.billing.anaesthesia.save" />
                    }
                </Button>
            </Box>
        </Box>
    );
};

export default AnaesthesiaActsPanel;

/**
 * Container: loads the percentage templates + surgical acts and persists the per-template
 * selection via the API. Lives in /billing/edit (Anaesthesia tab).
 */
export const AnaesthesiaActsConfiguration: React.FC<{ uuidInvestigation: string; idBillingInfo: number }> = ({ uuidInvestigation, idBillingInfo }) => {
    const [templates, setTemplates] = useState<PercentTemplate[]>([]);
    const [acts, setActs] = useState<AnaesthesiaAct[]>([]);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const resp = await getAnaesthesiaActsService(uuidInvestigation, idBillingInfo);
            setTemplates(resp.templates || []);
            setActs(resp.acts || []);
        } catch (e) {
            setTemplates([]);
            setActs([]);
        }
    }, [uuidInvestigation, idBillingInfo]);

    useEffect(() => { if (uuidInvestigation && idBillingInfo) load(); }, [uuidInvestigation, idBillingInfo, load]);

    const onSave = async (templateId: number, ids: number[]) => {
        setSaving(true);
        try {
            await setAnaesthesiaActsService(uuidInvestigation, idBillingInfo, templateId, ids);
            await load();
        } finally {
            setSaving(false);
        }
    };

    // Re-key on the loaded links so the panel re-seeds its checkbox state after a save/reload.
    const seedKey = `${templates.map((t) => t.id).join(',')}|${acts.map((a) => `${a.id}:${a.linkedTemplateIds.join('.')}`).join(',')}`;
    return <AnaesthesiaActsPanel key={seedKey} templates={templates} acts={acts} onSave={onSave} saving={saving} />;
};
